import { LibraDashboardConfig } from 'features/libra/dashboard/libraDashboardApi'
import { LibraDashboardGroupSelect } from 'features/libra/dashboard/LibraDashboardGroupSelect'
import {
  LibraTicketGroup,
  useLibraGroups,
} from 'features/libra/ticket/libraTicketsApi'
import { type FC, useMemo, useState } from 'react'
import styled from 'styled-components'

import { LibraDashboardContent } from './contentView/LibraDashboardContent'

type LibraDashboardViewProps = {
  config: LibraDashboardConfig
}

export const LibraDashboardView: FC<LibraDashboardViewProps> = ({ config }) => {
  const { title, tags } = config

  // Query all groups for the organization (not filtered by dashboard config)
  const { groups: libraGroups, forceRefresh } = useLibraGroups({
    tags,
  })
  const {
    selectedGroupName,
    setSelectedGroupName,
    groupListOptions,
    selectedGroups,
  } = useGroupsUnifiedByName(libraGroups)

  const organizationId = tags?.organization as string | undefined

  return (
    <ViewContainer>
      <Heading>{title}</Heading>
      <BodyLayout>
        <GroupColumn>
          <LibraDashboardGroupSelect
            groups={groupListOptions}
            selectedGroupId={selectedGroupName}
            setSelectedGroupId={setSelectedGroupName}
            organizationId={organizationId}
            onGroupCreated={forceRefresh}
          />
        </GroupColumn>
        <DetailColumn>
          {selectedGroups ? (
            <LibraDashboardContent config={config} groups={selectedGroups} />
          ) : (
            <DetailSplash>Select a Group</DetailSplash>
          )}
        </DetailColumn>
      </BodyLayout>
    </ViewContainer>
  )
}

const useGroupsUnifiedByName = (
  libraGroups: ReadonlyArray<LibraTicketGroup>
): {
  selectedGroupName: string | undefined
  setSelectedGroupName: React.Dispatch<React.SetStateAction<string | undefined>>
  groupListOptions: ReadonlyArray<{ id: string; title: string }>
  selectedGroups: ReadonlyArray<LibraTicketGroup> | undefined
} => {
  const groupNameToGroups = useMemo<
    ReadonlyMap<string, ReadonlyArray<LibraTicketGroup>>
  >(() => {
    return libraGroups.reduce((acc, group) => {
      const list = acc.get(group.title) ?? []
      const newList = [...list, group]
      acc.set(group.title, newList)
      return acc
    }, new Map<string, ReadonlyArray<LibraTicketGroup>>())
  }, [libraGroups])

  const groupListOptions = useMemo(
    () =>
      Array.from(
        groupNameToGroups.entries().map(([title]) => ({ id: title, title }))
      ),
    [groupNameToGroups]
  )

  const [selectedGroupName, setSelectedGroupName] = useState<
    string | undefined
  >(undefined)

  const selectedGroups = useMemo(() => {
    return selectedGroupName
      ? groupNameToGroups.get(selectedGroupName)
      : undefined
  }, [groupNameToGroups, selectedGroupName])

  return {
    selectedGroupName,
    setSelectedGroupName,
    groupListOptions,
    selectedGroups,
  }
}

const ViewContainer = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.75rem 1.5rem 1.5rem 1.5rem;
`

const Heading = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.forecast.activeText};
`

const BodyLayout = styled.div`
  display: flex;
  gap: 1.5rem;
  min-height: 0;
  flex: 1 1 auto;
`

const GroupColumn = styled.div`
  width: 20%;
  min-width: 12rem;
  max-width: 16rem;
`

const DetailColumn = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const DetailSplash = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
`
